import { useContext, useEffect, useState } from 'react'
import { Card, Icon, Table } from '../gsys-ui'
import haxios from '../lib/haxios'
import useApi from '../hooks/useApi'
import moment from 'moment'
import { PencilIcon, TrashIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { Checkbox, NativeSelect, TextInput, Button, ActionIcon, Textarea, Group, Radio, Chip } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import { errorToast, successToast } from '../lib/helpers'
import { GiPalmTree } from 'react-icons/gi'
import { GlobalContext } from '../context/GlobalContext'
import { ClockIcon } from 'lucide-react'

const Holidays = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1)
  const [selectedYear, setSelectedYear] = useState(moment().year())
  const { uinfo } = useContext(GlobalContext)

  const {
    data: holidayRequests,
    isLoading,
    refetch
  } = useApi({
    url: '/salesassist/cplusassist/holidayrequests'
  })

  const { data: ustafferator, isLoading: isLoadingUstaff } = useApi({
    url: `/salesassistnew/ustafferators/${uinfo.staffId}`
  })

  const { data: bookedHolidays, isLoading: isLoadingBookedHolidays } = useApi({
    url: 'https://api.aa-vision.com/api/tma/holidays',
    method: 'POST',
    data: {
      StartDate: moment()
        .year(selectedYear)
        .month(selectedMonth - 1)
        .startOf('month')
        .format('YYYY-MM-DD'),
      EndDate: moment()
        .year(selectedYear)
        .month(selectedMonth - 1)
        .endOf('month')
        .format('YYYY-MM-DD'),
      StaffID: uinfo.staffId
    }
  })

  if (isLoading || isLoadingUstaff || isLoadingBookedHolidays) return null

  const dataFiltered = holidayRequests.filter((req) => {
    const requestDate = moment(req.dtStart)
    return (
      req.staffId === uinfo.staffId && requestDate.month() + 1 === selectedMonth && requestDate.year() === selectedYear
    )
  })

  console.log(uinfo)

  const handleNewRequest = () => {
    modals.openContextModal({
      modal: 'holidayRequestModal',
      title: 'New holiday request',
      centered: true,
      innerProps: {
        staffId: uinfo.staffId,
        refetch
      }
    })
  }

  const totalRequests = dataFiltered.length
  const rejectedRequests = dataFiltered.filter((request) => request.decision === 'REJECTED').length
  const approvedRequests = dataFiltered.filter((request) => request.decision === 'APPROVED').length
  const pendingRequests = dataFiltered.filter((request) => request.decision === 'PENDING').length

  const allocation = ustafferator.HolidayAllocation
  const totalBooked = Array.isArray(bookedHolidays)
    ? bookedHolidays.reduce((total, holiday) => {
        const start = moment(holiday.StartDatetime)
        const end = moment(holiday.EndDatetime)
        return total + end.diff(start, 'days') + 1
      }, 0)
    : 0

  return (
    <Card>
      <Card.Title>
        <div className="flex items-center space-x-2">
          <Icon Comp={GiPalmTree} />
          <div>My holidays</div>
        </div>
        <div className="flex items-center space-x-2 font-normal">
          <div className="text-lg font-semibold">
            {totalBooked}/{allocation} days booked
          </div>
          <div className="text-sm">({allocation - totalBooked} remaining)</div>
        </div>
        <div className="flex items-center space-x-4 font-normal">
          <div>
            <span>Total: </span>
            {totalRequests}
          </div>
          <div>
            <span>Rejected: </span>
            {rejectedRequests}
          </div>
          <div>
            <span>Approved: </span>
            {approvedRequests}
          </div>
          <div>
            <span>Pending: </span>
            {pendingRequests}
          </div>
        </div>
        <div className="flex items-center space-x-2 font-normal">
          <NativeSelect
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.currentTarget.value))}
            data={[
              { value: 1, label: 'January' },
              { value: 2, label: 'February' },
              { value: 3, label: 'March' },
              { value: 4, label: 'April' },
              { value: 5, label: 'May' },
              { value: 6, label: 'June' },
              { value: 7, label: 'July' },
              { value: 8, label: 'August' },
              { value: 9, label: 'September' },
              { value: 10, label: 'October' },
              { value: 11, label: 'November' },
              { value: 12, label: 'December' }
            ]}
          />
          <NativeSelect
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.currentTarget.value))}
            data={Array.from({ length: 2024 - moment().year() + 2 }, (_, i) => 2024 + i).map((num) => ({
              value: num,
              label: num
            }))}
          />
          <Button onClick={handleNewRequest}>New request</Button>
        </div>
      </Card.Title>
      <Table
        data={dataFiltered}
        columns={[
          {
            header: 'Start date',
            id: 'start',
            cell: ({ row }) => moment(row.original.dtStart).format('DD/MM/YYYY')
          },
          {
            header: 'End date',
            id: 'end',
            cell: ({ row }) => moment(row.original.dtEnd).format('DD/MM/YYYY')
          },
          {
            header: 'Days',
            id: 'days',
            cell: ({ row }) => moment(row.original.dtEnd).diff(moment(row.original.dtStart), 'days') + 1
          },
          {
            header: 'Reason',
            accessorKey: 'reason'
          },
          {
            header: 'Status',
            id: 'status',
            cell: ({ row }) =>
              row.original.decision === 'APPROVED' ? (
                <Chip color="green" checked={true} size="md">
                  Approved
                </Chip>
              ) : row.original.descision === 'REJECTED' ? (
                <Chip color="red" checked={true} icon={<Icon Comp={XMarkIcon} size={4} />} size="md">
                  Rejected
                </Chip>
              ) : (
                <Chip color="orange" checked={true} icon={<Icon Comp={ClockIcon} size={4} />} size="md">
                  Pending
                </Chip>
              )
          },
          {
            header: 'Notes',
            accessorKey: 'Notes'
          },
          {
            header: '',
            id: 'edit',
            width: 1,
            cell: ({ row }) => <Actions row={row.original} refetch={refetch} uinfo={uinfo} />
          }
        ]}
      />
    </Card>
  )
}

const Actions = ({ row, refetch, uinfo }) => {
  const handleDelete = (id) => {
    modals.openContextModal({
      modal: 'warningModal',
      withCloseButton: false,
      centered: true,
      innerProps: {
        message: `You are about to delete your holiday request for ${moment(row.dtStart).format(
          'DD/MM/YYYY'
        )} to ${moment(row.dtEnd).format('DD/MM/YYYY')}.`,
        submit: async () => {
          await haxios.delete(`/salesassist/cplusassist/holidayrequests/${row._id}`)
          await refetch()
          successToast(`Holiday request deleted.`)
        }
      }
    })
  }

  const handleEditRequest = () => {
    modals.openContextModal({
      modal: 'holidayRequestModal',
      title: 'Edit holiday request',
      centered: true,
      innerProps: {
        staffId: uinfo.staffId,
        refetch,
        editing: true,
        reqId: row._id,
        defaultValues: {
          startDate: moment(row.dtStart).toDate(),
          endDate: moment(row.dtEnd).toDate(),
          reason: row.reason
        }
      }
    })
  }

  return (
    <div className="flex space-x-1">
      <ActionIcon variant="default" onClick={handleEditRequest}>
        <Icon Comp={PencilIcon} size={4.5} />
      </ActionIcon>
      <ActionIcon variant="default" onClick={handleDelete}>
        <Icon Comp={TrashIcon} size={4.5} />
      </ActionIcon>
    </div>
  )
}

export const HolidayEditModal = ({ context, id, innerProps }) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  const onSubmit = (data) => console.log(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <NativeSelect label="Staff member" {...register('StaffID')} />
        <Checkbox label="Paid" />
        <TextInput label="Paid hours per day" {...register('HoursPerDay')} />
        <div className="font-bold">Total hours to be paid: todo</div>
        <Textarea label="Reason" {...register('Reason')} />
        <DateInput label="Start date" {...register('StartDateTime')} />
        <DateInput label="End date (inclusive)" {...register('EndDateTime')} />
        <Group>
          <Radio label="All day" />
          <Radio label="AM" />
          <Radio label="PM" />
        </Group>
        <Button>Submit</Button>
      </form>
    </>
  )
}

export default Holidays
